/**
 * Proforma composer (ADR-0032) — invoice-editor visual twin for draft
 * proformas. Reuses the invoice composer primitives wherever the document
 * shape is shared; the action semantics remain proforma-only: save draft or
 * finalize a non-fiscal quote.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, FileText, Trash2 } from "lucide-react";
import {
  Alert,
  Card,
  CardBody,
  ConfirmDialog,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { CustomerPicker, type CustomerSummary } from "../../invoices/customer-picker.tsx";
import { DatesAndNotesSection } from "../../invoices/dates-and-notes-section.tsx";
import { AiHint, AiTrailingButton } from "../../invoices/ai-hint.tsx";
import { LineItemsTable, type ComposerLineRow } from "../../invoices/line-items-table.tsx";
import { LivePreviewPanel } from "../../invoices/live-preview-panel.tsx";
import { RiskCard, type RiskState } from "../../invoices/risk-card.tsx";
import { SectionHeading } from "../../invoices/section-heading.tsx";
import { formatRupees } from "../../invoices/lib/format-money.ts";
import {
  computeInvoiceTotals,
  computeLineForeign,
  computeLineTax,
  convertForeignRateToPaise,
} from "../../invoices/lib/gst-calc-shim.ts";
import {
  STATES_FOR_PICKER,
  stateCodeFromGstin,
  stateCodeFromName,
} from "../../invoices/lib/state-codes.ts";

export interface ProformaComposerPageProps {
  /** When set, the composer loads + edits this draft instead of creating. */
  loadProformaId?: string;
}

type TaxTreatment =
  | "taxable_cgst_sgst"
  | "taxable_igst"
  | "nil_rated"
  | "exempt"
  | "out_of_scope"
  | "export_lut"
  | "export_with_igst"
  | "sez_supply";

interface ProformaComposerState {
  draftId: string | null;
  customer: CustomerSummary | null;
  lines: ComposerLineRow[];
  placeOfSupplyCode: string;
  validityDays: number;
  issuedOn: string;
  expiresOn: string;
  poReference: string;
  customerNote: string;
  internalNote: string;
  declaration: string;
  currencyCode: "INR" | "USD" | "AED";
  fxRate: string;
  fxRateDate: string;
  taxTreatment: TaxTreatment;
}

const VALIDITY_OPTIONS = [
  { days: 7, label: "Valid 7 days" },
  { days: 15, label: "Valid 15 days" },
  { days: 30, label: "Valid 30 days" },
  { days: 45, label: "Valid 45 days" },
];

const CURRENCY_OPTIONS = [
  { code: "INR", label: "INR · Indian rupee" },
  { code: "USD", label: "USD · US dollar" },
  { code: "AED", label: "AED · UAE dirham" },
] as const;

const TAX_TREATMENT_DOMESTIC_OPTIONS: { value: TaxTreatment; label: string }[] = [
  { value: "taxable_cgst_sgst", label: "Taxable · CGST + SGST (intra-state)" },
  { value: "taxable_igst", label: "Taxable · IGST (inter-state)" },
  { value: "nil_rated", label: "Nil-rated (HSN attracts 0%)" },
  { value: "exempt", label: "Exempt supply" },
  { value: "out_of_scope", label: "Out of scope (composition / non-GST)" },
];

const TAX_TREATMENT_FOREIGN_OPTIONS: { value: TaxTreatment; label: string }[] = [
  { value: "export_lut", label: "Export under LUT — zero-rated, no IGST charged" },
  { value: "export_with_igst", label: "Export with IGST + refund" },
  { value: "sez_supply", label: "SEZ supply" },
];

const TAX_TREATMENT_ZERO_RATED_SET = new Set<TaxTreatment>([
  "nil_rated",
  "exempt",
  "out_of_scope",
  "export_lut",
  "sez_supply",
]);

const DEFAULT_VALIDITY_DAYS = 15;

const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tmp-${Math.random().toString(36).slice(2)}`;

function emptyLine(): ComposerLineRow {
  return {
    key: newKey(),
    itemId: null,
    description: "",
    hsn: "",
    qtyMilli: 1000,
    unit: "Nos",
    ratePaise: 0n,
    rateForeignMinor: null,
    discountBps: 0,
    taxRateBps: 1800,
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function termDaysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  const days = Math.round((to - from) / (1000 * 60 * 60 * 24));
  return Math.min(Math.max(days, 0), 365);
}

function defaultTaxTreatmentFor(currencyCode: string): TaxTreatment {
  return currencyCode === "INR" ? "taxable_cgst_sgst" : "export_lut";
}

export function ProformaComposerPage({ loadProformaId }: ProformaComposerPageProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const isEdit = Boolean(loadProformaId);

  const company = trpc.companies.get.useQuery({});
  const existing = trpc.proformas.get.useQuery(
    { proformaId: loadProformaId ?? "" },
    { enabled: isEdit },
  );
  const fxRatesQuery = trpc.fxRates.list.useQuery();
  const fxRateRupeesByCurrency = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of fxRatesQuery.data ?? []) {
      map[r.baseCurrency] = (Number(r.ratePaise) / 100).toString();
    }
    return map;
  }, [fxRatesQuery.data]);

  const today = todayIso();
  const [state, setState] = useState<ProformaComposerState>(() => ({
    draftId: null,
    customer: null,
    lines: [emptyLine()],
    placeOfSupplyCode: "",
    validityDays: DEFAULT_VALIDITY_DAYS,
    issuedOn: today,
    expiresOn: addDaysIso(today, DEFAULT_VALIDITY_DAYS),
    poReference: "",
    customerNote: "",
    internalNote: "",
    declaration: "",
    currencyCode: "INR",
    fxRate: "",
    fxRateDate: today,
    taxTreatment: "taxable_cgst_sgst",
  }));

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!isEdit || !existing.data) return;
    const p = existing.data.proforma;
    if (p.status !== "draft") {
      void navigate({ to: "/sales/proformas/$proformaId", params: { proformaId: p.id } });
      return;
    }
    hydratedRef.current = true;
    const c = existing.data.customer;
    setState({
      draftId: p.id,
      customer: {
        id: c.id,
        name: c.name,
        gstin: c.gstin,
        phone: c.phone,
        state: c.state,
        city: c.city,
        contactName: null,
        paymentTerms: null,
        creditLimitPaise: null,
      },
      lines: existing.data.lines.map((line) => ({
        key: newKey(),
        itemId: line.itemId,
        description: line.description,
        hsn: line.hsn,
        unit: line.unit,
        qtyMilli: line.qtyMilli,
        ratePaise: BigInt(line.ratePaise),
        rateForeignMinor:
          line.rateForeignMinor !== null ? BigInt(line.rateForeignMinor) : null,
        discountBps: line.discountBps,
        taxRateBps: line.taxRateBps,
      })),
      placeOfSupplyCode: p.placeOfSupply,
      validityDays: p.expiresOn ? termDaysBetween(p.issuedOn, p.expiresOn) : DEFAULT_VALIDITY_DAYS,
      issuedOn: p.issuedOn,
      expiresOn: p.expiresOn ?? addDaysIso(p.issuedOn, DEFAULT_VALIDITY_DAYS),
      poReference: p.poReference ?? "",
      customerNote: p.customerNote ?? "",
      internalNote: p.internalNote ?? "",
      declaration: p.declaration ?? "",
      currencyCode: (p.currencyCode as ProformaComposerState["currencyCode"]) ?? "INR",
      fxRate: p.fxRateToInrPaise ? minorToMajor(BigInt(p.fxRateToInrPaise)) : "",
      fxRateDate: p.fxRateDate ?? p.issuedOn,
      taxTreatment: (p.taxTreatment as TaxTreatment) ?? defaultTaxTreatmentFor(p.currencyCode),
    });
  }, [existing.data, isEdit, navigate]);

  const companyStateCode = useMemo(
    () =>
      stateCodeFromGstin(company.data?.gstin ?? null) ??
      stateCodeFromName(company.data?.state ?? null) ??
      "",
    [company.data?.gstin, company.data?.state],
  );

  const isForeign = state.currencyCode !== "INR";
  const fxRateToInrPaise = useMemo(() => majorToMinor(state.fxRate), [state.fxRate]);
  const forceZeroTax = TAX_TREATMENT_ZERO_RATED_SET.has(state.taxTreatment);

  const { computedLines, computedForeign, totals, foreignTotals } = useMemo(() => {
    const inrRates = state.lines.map((line) =>
      isForeign && line.rateForeignMinor !== null && fxRateToInrPaise > 0n
        ? convertForeignRateToPaise(line.rateForeignMinor, fxRateToInrPaise)
        : line.ratePaise,
    );
    const cls = state.lines.map((line, index) =>
      computeLineTax({
        qtyMilli: line.qtyMilli,
        ratePaise: inrRates[index] ?? 0n,
        discountBps: line.discountBps,
        taxRateBps: forceZeroTax ? 0 : line.taxRateBps,
      }),
    );
    const t = computeInvoiceTotals({
      lines: cls,
      companyStateCode,
      placeOfSupplyCode: state.placeOfSupplyCode || companyStateCode,
    });
    if (!isForeign) return { computedLines: cls, computedForeign: undefined, totals: t };
    const cf = state.lines.map((line) =>
      computeLineForeign({
        qtyMilli: line.qtyMilli,
        rateForeignMinor: line.rateForeignMinor ?? 0n,
        discountBps: line.discountBps,
        taxRateBps: forceZeroTax ? 0 : line.taxRateBps,
      }),
    );
    const ft = {
      taxableForeign: cf.reduce((a, c) => a + c.taxableForeignMinor, 0n),
      totalForeign: cf.reduce((a, c) => a + c.lineTotalForeignMinor, 0n),
    };
    return { computedLines: cls, computedForeign: cf, totals: t, foreignTotals: ft };
  }, [
    companyStateCode,
    forceZeroTax,
    fxRateToInrPaise,
    isForeign,
    state.lines,
    state.placeOfSupplyCode,
  ]);

  const onCustomerSelect = useCallback(
    (customer: CustomerSummary) => {
      setState((prev) => {
        const nextPos =
          prev.currencyCode !== "INR"
            ? "96"
            : stateCodeFromGstin(customer.gstin) ??
              stateCodeFromName(customer.state) ??
              prev.placeOfSupplyCode;
        const parsedTerms = parseTermDays(customer.paymentTerms);
        const validityDays = parsedTerms ?? prev.validityDays;
        return {
          ...prev,
          customer,
          placeOfSupplyCode: nextPos,
          validityDays,
          expiresOn: addDaysIso(prev.issuedOn, validityDays),
        };
      });
    },
    [],
  );

  const create = trpc.proformas.create.useMutation();
  const update = trpc.proformas.update.useMutation();
  const finalize = trpc.proformas.finalize.useMutation();
  const deleteDraft = trpc.proformas.delete.useMutation({
    onSuccess: async () => {
      await utils.proformas.list.invalidate();
      toast.success("Proforma draft deleted.");
      void navigate({ to: "/sales/proformas" });
    },
    onError: (error) => toast.error(error.message),
  });
  const [deleteOpen, setDeleteOpen] = useState(false);

  const saving = create.isPending || update.isPending;
  const busy = saving || finalize.isPending;
  const hasLine = state.lines.some((line) => line.description.trim().length > 0);
  const qtyError =
    state.lines.some((line) => line.description.trim().length > 0 && line.qtyMilli <= 0)
      ? "Line quantities must be greater than zero."
      : null;
  const fxError =
    isForeign && fxRateToInrPaise <= 0n ? "Enter an FX rate before saving this proforma." : null;
  const ready =
    !!state.customer && hasLine && /^\d{2}$/.test(state.placeOfSupplyCode) && !qtyError && !fxError;

  async function saveDraft(): Promise<string | null> {
    const payload = buildPayload();
    if (!payload) return null;
    try {
      const result =
        isEdit && loadProformaId
          ? await update.mutateAsync({ proformaId: loadProformaId, ...payload })
          : await create.mutateAsync(payload);
      await Promise.all([
        utils.proformas.list.invalidate(),
        utils.proformas.get.invalidate({ proformaId: result.proforma.id }),
      ]);
      toast.success(isEdit ? "Proforma saved." : "Proforma draft created.");
      return result.proforma.id;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save proforma.");
      return null;
    }
  }

  async function handleSaveDraft() {
    const id = await saveDraft();
    if (!id) return;
    void navigate({ to: "/sales/proformas/$proformaId", params: { proformaId: id } });
  }

  async function handleFinalize() {
    const id = await saveDraft();
    if (!id) return;
    try {
      const result = await finalize.mutateAsync({ proformaId: id });
      await Promise.all([
        utils.proformas.list.invalidate(),
        utils.proformas.get.invalidate({ proformaId: result.proforma.id }),
      ]);
      toast.success("Proforma finalized.");
      void navigate({ to: "/sales/proformas/$proformaId", params: { proformaId: result.proforma.id } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not finalize proforma.");
    }
  }

  function buildPayload() {
    if (!state.customer) {
      toast.error("Pick a customer first.");
      return null;
    }
    if (!/^\d{2}$/.test(state.placeOfSupplyCode)) {
      toast.error("Place of supply must be a 2-digit state code.");
      return null;
    }
    if (qtyError) {
      toast.error(qtyError);
      return null;
    }
    if (fxError) {
      toast.error(fxError);
      return null;
    }
    const validLines = state.lines.filter((line) => line.description.trim().length > 0);
    if (validLines.length === 0) {
      toast.error("Add at least one line with a description.");
      return null;
    }
    let foreignTotalMinor = 0n;
    const lines = validLines.map((line) => {
      const rateForeignMinor = line.rateForeignMinor ?? 0n;
      const derivedRatePaise =
        isForeign && fxRateToInrPaise > 0n
          ? convertForeignRateToPaise(rateForeignMinor, fxRateToInrPaise)
          : line.ratePaise;
      if (isForeign) {
        const foreign = computeLineForeign({
          qtyMilli: line.qtyMilli,
          rateForeignMinor,
          discountBps: line.discountBps,
          taxRateBps: forceZeroTax ? 0 : line.taxRateBps,
        });
        foreignTotalMinor += foreign.lineTotalForeignMinor;
      }
      return {
        ...(line.itemId ? { itemId: line.itemId } : {}),
        description: line.description.trim(),
        hsn: line.hsn || "9999",
        unit: line.unit || "Nos",
        qtyMilli: line.qtyMilli,
        ratePaise: derivedRatePaise.toString(),
        discountBps: line.discountBps,
        taxRateBps: forceZeroTax ? 0 : line.taxRateBps,
        ...(isForeign ? { rateForeignMinor: rateForeignMinor.toString() } : {}),
      };
    });
    return {
      customerId: state.customer.id,
      issuedOn: state.issuedOn,
      expiresOn: state.expiresOn,
      placeOfSupply: state.placeOfSupplyCode,
      lines,
      ...(state.customerNote.trim() ? { customerNote: state.customerNote.trim() } : {}),
      ...(state.internalNote.trim() ? { internalNote: state.internalNote.trim() } : {}),
      ...(state.poReference.trim() ? { poReference: state.poReference.trim() } : {}),
      ...(state.declaration.trim() ? { declaration: state.declaration.trim() } : {}),
      ...(isForeign
        ? {
            currencyCode: state.currencyCode,
            foreignAmountMinor: foreignTotalMinor.toString(),
            fxRateToInrPaise: fxRateToInrPaise.toString(),
            fxRateDate: state.fxRateDate || state.issuedOn,
          }
        : { currencyCode: "INR" }),
      taxTreatment: state.taxTreatment,
    };
  }

  if (company.isLoading || (isEdit && existing.isLoading)) {
    return <p className="p-6 text-sm text-muted">Loading proforma editor...</p>;
  }
  if (company.error || !company.data) {
    return (
      <div className="p-6">
        <Alert
          intent="bad"
          title="Couldn't load company profile"
          description={company.error?.message ?? "Settings -> Company profile is required."}
        />
      </div>
    );
  }
  if (!companyStateCode) {
    return (
      <div className="p-6">
        <Alert
          intent="warn"
          title="Configure your company GSTIN or state"
          description="Flint needs your seller state to compute the GST split. Visit Settings -> Company profile."
        />
      </div>
    );
  }

  const saveStatus = saving ? "Saving..." : state.draftId ? "Saved draft" : "Unsaved";
  const saveStatusTone: SaveStatusTone = saving ? "saving" : state.draftId ? "saved" : "idle";
  const creditLimitPaise = state.customer?.creditLimitPaise
    ? BigInt(state.customer.creditLimitPaise)
    : null;
  const gstinState: RiskState = state.customer ? "ok" : "pending";
  const gstinDetail = state.customer
    ? state.customer.gstin
      ? "Customer GSTIN available; place-of-supply is set."
      : "Unregistered customer; GST split uses place-of-supply."
    : "Pick a customer to check GST context.";
  const discountState: RiskState = state.lines.some((line) => line.discountBps > 1500)
    ? "warn"
    : "ok";
  const discountDetail =
    discountState === "warn"
      ? "One or more discounts are above 15%; founder approval may be needed on conversion."
      : "Discounts are within the standard threshold.";
  const creditState: RiskState = creditLimitPaise ? "ok" : "pending";
  const creditDetail = creditLimitPaise
    ? `Limit ${formatRupees(creditLimitPaise)} on customer record.`
    : "Credit-limit check applies when this proforma converts to invoice.";

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 flex min-h-14 shrink-0 flex-col items-stretch gap-3 border-b border-line bg-paper px-6 py-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <Link
            to="/sales/proformas"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line bg-paper text-ink-2 transition-colors hover:bg-bg"
            aria-label="Back to proformas"
          >
            <ArrowLeft size={16} strokeWidth={2.2} />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 leading-none">
              <h1 className="text-base font-bold leading-tight text-ink">
                {isEdit ? "Edit proforma" : "New proforma"}
              </h1>
              <span className="inline-flex items-center rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                Draft · pending PRO number
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted">
              Create a non-fiscal draft, review the totals, then finalize when the quote is ready.
            </p>
          </div>
        </div>
        <span className={saveStatusClassName(saveStatusTone)} aria-live="polite">
          <span aria-hidden className={saveStatusDotClassName(saveStatusTone)} />
          {saveStatus}
        </span>
      </header>

      <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-5 px-6 py-5 xl:grid-cols-5">
        <div className="flex min-w-0 flex-col gap-4 xl:col-span-3">
          <Card>
            <SectionHeading
              step={1}
              title="Customer & billing context"
              description="Who the proforma is for. This decides the tax mode."
              trailing={
                <AiTrailingButton
                  label="Suggest customer"
                  title="AI customer suggestions land in V1.x."
                />
              }
            />
            <CardBody className="flex flex-col gap-3.5">
              <CustomerPicker selected={state.customer} onSelect={onCustomerSelect} disabled={busy} />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pos-state">
                    Place of supply
                    <span aria-hidden className="ml-0.5 text-rose-600">
                      *
                    </span>
                  </Label>
                  <Select
                    value={state.placeOfSupplyCode}
                    onValueChange={(value) =>
                      setState((prev) => ({ ...prev, placeOfSupplyCode: value }))
                    }
                    disabled={busy}
                  >
                    <SelectTrigger id="pos-state" aria-label="Place of supply">
                      <SelectValue placeholder="Pick a state..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES_FOR_PICKER.map((stateOption) => (
                        <SelectItem key={stateOption.code} value={stateOption.code}>
                          {stateOption.name} ({stateOption.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted">Auto-derived from customer GSTIN.</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="validity">Validity</Label>
                  <Select
                    value={String(state.validityDays)}
                    onValueChange={(value) => {
                      const days = Number(value);
                      setState((prev) => ({
                        ...prev,
                        validityDays: days,
                        expiresOn: addDaysIso(prev.issuedOn, days),
                      }));
                    }}
                    disabled={busy}
                  >
                    <SelectTrigger id="validity" aria-label="Validity">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VALIDITY_OPTIONS.map((option) => (
                        <SelectItem key={option.days} value={String(option.days)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted">Sets the valid-until date.</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Document status</Label>
                  <div className="flex h-9 items-center gap-2">
                    <StatusPill tone="muted" size="sm">
                      Non-fiscal
                    </StatusPill>
                    <span className="text-sm text-muted">No ledger posting</span>
                  </div>
                  <p className="text-sm text-muted">Conversion creates the real tax invoice later.</p>
                </div>
              </div>

              <AiHint
                body={
                  <>
                    <strong className="text-ink-2">Flint:</strong> quote acceptance nudges and
                    follow-up suggestions appear here once the AI vertical ships (V1.x).
                  </>
                }
              />
            </CardBody>
          </Card>

          <Card>
            <SectionHeading
              step={2}
              title="Currency & tax treatment"
              description={
                isForeign
                  ? "Lines are entered in proforma currency. INR equivalent is derived from the booked FX rate."
                  : "Proforma is quoted and displayed in INR."
              }
            />
            <CardBody className="flex flex-col gap-3.5">
              <div className={`grid grid-cols-1 gap-3.5 ${isForeign ? "sm:grid-cols-3" : ""}`}>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="proforma-currency">Currency</Label>
                  <Select
                    value={state.currencyCode}
                    onValueChange={(value) => {
                      const next = value as ProformaComposerState["currencyCode"];
                      setState((prev) => {
                        const becomingForeign = next !== "INR";
                        return {
                          ...prev,
                          currencyCode: next,
                          placeOfSupplyCode:
                            becomingForeign && prev.placeOfSupplyCode !== "96"
                              ? "96"
                              : !becomingForeign && prev.placeOfSupplyCode === "96"
                                ? companyStateCode
                                : prev.placeOfSupplyCode,
                          taxTreatment: defaultTaxTreatmentFor(next),
                          fxRate:
                            becomingForeign && prev.fxRate === ""
                              ? fxRateRupeesByCurrency[next] ?? ""
                              : prev.fxRate,
                          lines: prev.lines.map((line) => ({
                            ...line,
                            rateForeignMinor: becomingForeign
                              ? line.rateForeignMinor ?? 0n
                              : null,
                          })),
                        };
                      });
                    }}
                    disabled={busy}
                  >
                    <SelectTrigger id="proforma-currency" aria-label="Currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isForeign ? (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="fx-rate">FX rate to INR</Label>
                      <Input
                        id="fx-rate"
                        type="text"
                        inputMode="decimal"
                        value={state.fxRate}
                        disabled={busy}
                        onChange={(event) =>
                          setState((prev) => ({ ...prev, fxRate: decimalInput(event.target.value) }))
                        }
                        placeholder={fxRateRupeesByCurrency[state.currencyCode] ?? "e.g. 95.70"}
                        className="font-mono"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="fx-date">Rate date</Label>
                      <Input
                        id="fx-date"
                        type="date"
                        value={state.fxRateDate}
                        disabled={busy}
                        onChange={(event) =>
                          setState((prev) => ({ ...prev, fxRateDate: event.target.value }))
                        }
                        className="font-mono"
                      />
                    </div>
                  </>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="tax-treatment">Tax treatment</Label>
                <Select
                  value={state.taxTreatment}
                  disabled={busy}
                  onValueChange={(value) =>
                    setState((prev) => {
                      const nextTreatment = value as TaxTreatment;
                      const zeroRated = TAX_TREATMENT_ZERO_RATED_SET.has(nextTreatment);
                      return {
                        ...prev,
                        taxTreatment: nextTreatment,
                        lines: zeroRated
                          ? prev.lines.map((line) => ({ ...line, taxRateBps: 0 }))
                          : prev.lines,
                      };
                    })
                  }
                >
                  <SelectTrigger id="tax-treatment" aria-label="Tax treatment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(state.currencyCode === "INR"
                      ? TAX_TREATMENT_DOMESTIC_OPTIONS
                      : TAX_TREATMENT_FOREIGN_OPTIONS
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted">
                  {forceZeroTax
                    ? "Zero-rated — every line item must have tax rate 0%."
                    : "Tax is computed now so conversion to invoice does not surprise the customer."}
                </span>
              </div>
            </CardBody>
          </Card>

          <Card>
            <SectionHeading
              step={3}
              title="Line items"
              description={
                isForeign
                  ? `Rates entered in ${state.currencyCode}. INR equivalent computed via the booked FX rate.`
                  : "What's being quoted. HSN, qty, rate, GST."
              }
              trailing={<AiTrailingButton label="Lookup HSN" title="AI HSN lookup lands in V1.x." />}
            />
            <CardBody className="flex flex-col gap-3.5">
              <LineItemsTable
                rows={state.lines}
                computed={computedLines}
                {...(computedForeign ? { computedForeign } : {})}
                currencyCode={state.currencyCode}
                disabled={busy}
                descriptionPlaceholder="Description"
                ratePlaceholder="Rate ₹"
                addLabel="Add line"
                onChange={(key, patch) =>
                  setState((prev) => ({
                    ...prev,
                    lines: prev.lines.map((line) =>
                      line.key === key ? { ...line, ...patch } : line,
                    ),
                  }))
                }
                onAdd={() => setState((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }))}
                onRemove={(key) =>
                  setState((prev) => ({
                    ...prev,
                    lines:
                      prev.lines.length === 1
                        ? prev.lines
                        : prev.lines.filter((line) => line.key !== key),
                  }))
                }
                summary={
                  <>
                    {state.lines.length} item{state.lines.length === 1 ? "" : "s"} ·{" "}
                    {isForeign && foreignTotals ? (
                      <>
                        <span className="font-mono">
                          {formatForeign(state.currencyCode, foreignTotals.taxableForeign)}
                        </span>{" "}
                        taxable
                      </>
                    ) : (
                      <>
                        <span className="font-mono">
                          ₹{formatRupees(totals.taxableValuePaise).replace("₹", "")}
                        </span>{" "}
                        taxable · {totals.taxMode === "intra" ? "CGST + SGST" : "IGST"}
                      </>
                    )}
                  </>
                }
              />
              <p className="text-right text-sm text-muted">
                Estimated total{" "}
                <span className="font-semibold text-ink">{formatRupees(totals.grandTotalPaise)}</span>
                <span className="ml-1 text-xs">(server recomputes on save)</span>
              </p>
            </CardBody>
          </Card>

          {isForeign ? (
            <Card>
              <SectionHeading
                step={4}
                title="FX preview & INR posting"
                description="Read-only. The customer sees the foreign side; conversion books the INR side."
              />
              <CardBody className="flex flex-col gap-3">
                <div className="grid grid-cols-1 gap-3 rounded-sm border border-line bg-raised px-3 py-3 sm:grid-cols-3">
                  <Metric
                    label={`Foreign total (${state.currencyCode})`}
                    value={
                      foreignTotals
                        ? formatForeign(state.currencyCode, foreignTotals.totalForeign)
                        : "-"
                    }
                  />
                  <Metric
                    label="Booked FX rate"
                    value={fxRateToInrPaise > 0n ? `₹${state.fxRate}/${state.currencyCode}` : "-"}
                  />
                  <Metric label="INR quote value" value={formatRupees(totals.grandTotalPaise)} />
                </div>
                {fxError ? <p className="text-xs font-medium text-rose-700">{fxError}</p> : null}
              </CardBody>
            </Card>
          ) : null}

          <DatesAndNotesSection
            issuedOn={state.issuedOn}
            dueOn={state.expiresOn}
            poReference={state.poReference}
            customerNote={state.customerNote}
            internalNote={state.internalNote}
            declaration={state.declaration}
            documentLabel="proforma"
            title="Dates, validity & notes"
            description="When this quote is issued, when it expires, and what prints for the customer."
            issuedLabel="Proforma date"
            dueLabel="Valid until"
            customerNotePlaceholder="Printed on the proforma"
            customerNoteVisibilityLabel="appears on proforma"
            declarationVisibilityLabel="appears on proforma PDF"
            declarationPlaceholder="One declaration per line. Empty = use your company default."
            onChange={(patch) =>
              setState((prev) => ({
                ...prev,
                issuedOn: patch.issuedOn ?? prev.issuedOn,
                expiresOn: patch.dueOn ?? prev.expiresOn,
                poReference: patch.poReference ?? prev.poReference,
                customerNote: patch.customerNote ?? prev.customerNote,
                internalNote: patch.internalNote ?? prev.internalNote,
                declaration: patch.declaration ?? prev.declaration,
              }))
            }
            dueHint={
              state.validityDays > 0
                ? `Auto from ${state.validityDays}-day validity · ${state.expiresOn}`
                : undefined
            }
            disabled={busy}
          />

          <RiskCard
            gstinState={gstinState}
            gstinDetail={gstinDetail}
            discountState={discountState}
            discountDetail={discountDetail}
            creditState={creditState}
            creditDetail={creditDetail}
          />
        </div>

        <aside className="flex flex-col gap-3 xl:sticky xl:top-4 xl:col-span-2 xl:self-start">
          <LivePreviewPanel
            seller={{
              name: company.data.legalName,
              addressLines: [
                company.data.address ?? "",
                [company.data.city, company.data.state, company.data.pincode]
                  .filter(Boolean)
                  .join(", "),
              ].filter((line) => line.trim().length > 0),
              gstin: company.data.gstin,
              state: company.data.state,
              bankName: company.data.bankName,
              bankAccountNumber: company.data.bankAccountNumber,
              bankIfsc: company.data.bankIfsc,
              bankUpiId: company.data.bankUpiId,
            }}
            customer={
              state.customer
                ? {
                    name: state.customer.name,
                    gstin: state.customer.gstin,
                    state: state.customer.state,
                    city: state.customer.city,
                  }
                : { name: "- select customer -", gstin: null, state: null, city: null }
            }
            docNumber={null}
            issuedOn={state.issuedOn}
            dueOn={state.expiresOn}
            placeOfSupplyCode={state.placeOfSupplyCode || companyStateCode}
            lines={state.lines.map((line, index) => ({
              position: index + 1,
              description: line.description,
              hsn: line.hsn,
              qtyMilli: line.qtyMilli,
              unit: line.unit,
              ratePaise: line.ratePaise,
              lineTotalPaise: computedLines[index]?.lineTotalPaise ?? 0n,
              taxRateBps: forceZeroTax ? 0 : line.taxRateBps,
            }))}
            totals={totals}
            customerNote={state.customerNote || undefined}
            status="draft"
            documentLabel="Proforma invoice"
            detailLabel="Proforma details"
            previewTitle="Proforma preview"
            documentSummary="Proforma draft"
          />
        </aside>
      </div>

      <footer className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-2.5 border-t border-line bg-paper px-6 py-3 shadow-lg">
        <p className="min-w-0 flex-1 text-xs text-muted">
          {ready ? (
            <>
              <strong className="text-ink">Ready to save.</strong> Finalize only when the customer
              should see a numbered proforma.
            </>
          ) : qtyError ? (
            <span className="text-rose-700" role="alert">
              {qtyError}
            </span>
          ) : fxError ? (
            <span className="text-rose-700" role="alert">
              {fxError}
            </span>
          ) : (
            "Pick a customer + add at least one line with a description."
          )}
        </p>
        <div className="flex items-center gap-2">
          {isEdit && state.draftId ? (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={busy || deleteDraft.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-bad-100 bg-bad-50 px-3 text-xs font-semibold text-bad transition-colors hover:border-bad hover:bg-bad-100 disabled:pointer-events-none disabled:opacity-50"
            >
              <Trash2 size={14} />
              <span>Delete draft</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate({ to: "/sales/proformas" })}
            disabled={busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-sm px-3 text-sm font-medium text-ink-2 transition-colors hover:bg-bg hover:text-ink disabled:pointer-events-none disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSaveDraft();
            }}
            disabled={busy}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink transition-colors hover:border-line-2 hover:bg-bg disabled:pointer-events-none disabled:opacity-50"
          >
            <FileText size={14} />
            <span>{saving ? "Saving..." : isEdit ? "Save changes" : "Create draft"}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              void handleFinalize();
            }}
            disabled={busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-sm bg-ink px-3.5 text-sm font-semibold text-paper transition-colors hover:bg-ink-2 disabled:pointer-events-none disabled:opacity-50"
          >
            <ArrowRight size={14} strokeWidth={2.2} />
            <span>{finalize.isPending ? "Finalizing..." : "Finalize proforma"}</span>
          </button>
        </div>
      </footer>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this draft?"
        description={
          state.customer
            ? `${state.customer.name}'s proforma draft will be removed permanently. No PRO number has been issued.`
            : "This proforma draft will be removed permanently. No PRO number has been issued."
        }
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting..." : "Delete draft"}
        onConfirm={() => {
          if (!state.draftId) return;
          deleteDraft.mutate({ proformaId: state.draftId });
        }}
      />
    </div>
  );
}

type SaveStatusTone = "idle" | "saving" | "saved";

function saveStatusClassName(tone: SaveStatusTone): string {
  const base =
    "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap";
  if (tone === "saving") return `${base} bg-amber-50 text-amber-700`;
  if (tone === "saved") return `${base} bg-emerald-50 text-emerald-700`;
  return `${base} bg-raised text-muted`;
}

function saveStatusDotClassName(tone: SaveStatusTone): string {
  const base = "h-1.5 w-1.5 rounded-full";
  if (tone === "saving") return `${base} animate-pulse bg-amber-500`;
  if (tone === "saved") return `${base} bg-emerald-500`;
  return `${base} bg-line-2`;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
      <div className="font-mono text-base font-semibold text-ink">{value}</div>
    </div>
  );
}

function decimalInput(value: string): string {
  return value
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1")
    .slice(0, 16);
}

function majorToMinor(value: string): bigint {
  const cleaned = value.replace(/[,\s]/g, "");
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === "" || cleaned === ".") return 0n;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2) || "0");
}

function minorToMajor(value: bigint): string {
  const sign = value < 0n ? "-" : "";
  const abs = value < 0n ? -value : value;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

function formatForeign(currencyCode: string, minor: bigint): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(minor) / 100);
}

function parseTermDays(input: string | null): number | null {
  if (!input) return null;
  const match = /(\d+)/.exec(input);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value >= 0 && value <= 365 ? value : null;
}
